import { useSyncExternalStore } from 'react';
import { UserDto } from '../api/authApi';
import { setAccessToken } from '../api/apiClient';

interface AuthState {
  user: UserDto | null;
  accessToken: string | null;
  selectedOrgId: string | null;
  selectedSubOrgId: string | null;
  isAuthenticated: boolean;
  setAuth: (user: UserDto, token: string, refreshToken: string) => void;
  setAccessTokenState: (token: string | null) => void;
  setUser: (user: UserDto) => void;
  setContext: (orgId: string | null, subOrgId: string | null) => void;
  logout: () => void;
  hasPermission: (permission: string) => boolean;
  hasRole: (role: string) => boolean;
}

type AuthSnapshot = AuthState;

const resolveContext = (user: UserDto | null, orgId: string | null, subOrgId: string | null) => {
  const orgs = user?.organisations || [];
  if (orgs.length === 0) return { selectedOrgId: null, selectedSubOrgId: null };
  const selectedOrg = orgs.find((o) => o.organisationId === orgId) || orgs[0];
  const subOrgs = selectedOrg.subOrganisations || [];
  let selectedSubOrgId: string | null = null;
  if (subOrgId && subOrgs.some((s) => s.subOrganisationId === subOrgId)) {
    selectedSubOrgId = subOrgId;
  } else if (!selectedOrg.canAccessOrganisationLevel && subOrgs.length > 0) {
    selectedSubOrgId = subOrgs[0].subOrganisationId;
  }
  return { selectedOrgId: selectedOrg.organisationId, selectedSubOrgId };
};

const persistContext = (orgId: string | null, subOrgId: string | null) => {
  if (orgId) localStorage.setItem('selectedOrgId', orgId);
  else localStorage.removeItem('selectedOrgId');
  if (subOrgId) localStorage.setItem('selectedSubOrgId', subOrgId);
  else localStorage.removeItem('selectedSubOrgId');
};

const normalizeRole = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, '');

const hasPermissionValue = (permissions: string[] | undefined, permission: string): boolean => {
  if (!permissions?.length) return false;
  if (permissions.includes(permission)) return true;
  if (permission.endsWith('.manage')) return false;

  if (permission.startsWith('admin.permissions.') && permissions.includes('admin.roles.manage')) {
    return true;
  }

  const parts = permission.split('.');
  if (parts.length < 3) return false;
  return permissions.includes(`${parts[0]}.${parts[1]}.manage`);
};

const readPersistedAuth = () => {
  try {
    const stored = localStorage.getItem('mmc-auth');
    if (!stored) return {};
    const parsed = JSON.parse(stored);
    return parsed?.state || parsed || {};
  } catch {
    return {};
  }
};

const writePersistedAuth = (state: AuthSnapshot) => {
  const persistedState = {
    user: state.user,
    isAuthenticated: state.isAuthenticated,
    selectedOrgId: state.selectedOrgId,
    selectedSubOrgId: state.selectedSubOrgId,
  };
  localStorage.setItem('mmc-auth', JSON.stringify({ state: persistedState, version: 0 }));
};

const createAuthStore = () => {
  const listeners = new Set<() => void>();
  const persisted = readPersistedAuth();
  const context = resolveContext(
    persisted.user || null,
    persisted.selectedOrgId || localStorage.getItem('selectedOrgId'),
    persisted.selectedSubOrgId || localStorage.getItem('selectedSubOrgId')
  );
  persistContext(context.selectedOrgId, context.selectedSubOrgId);
  setAccessToken(null);

  let snapshot: AuthSnapshot = {
    user: persisted.user || null,
    accessToken: null,
    selectedOrgId: context.selectedOrgId,
    selectedSubOrgId: context.selectedSubOrgId,
    isAuthenticated: Boolean(persisted.user),
    setAuth: (user, token, refreshToken) => {
      void refreshToken;
      setAccessToken(token);
      const storedOrgId = localStorage.getItem('selectedOrgId');
      const storedSubOrgId = localStorage.getItem('selectedSubOrgId');
      const nextContext = resolveContext(user, storedOrgId, storedSubOrgId);
      persistContext(nextContext.selectedOrgId, nextContext.selectedSubOrgId);
      setSnapshot({
        user,
        accessToken: token,
        isAuthenticated: true,
        selectedOrgId: nextContext.selectedOrgId,
        selectedSubOrgId: nextContext.selectedSubOrgId,
      });
    },
    setAccessTokenState: (token) => {
      setSnapshot({ accessToken: token });
    },
    setUser: (user) => {
      const nextContext = resolveContext(user, snapshot.selectedOrgId, snapshot.selectedSubOrgId);
      persistContext(nextContext.selectedOrgId, nextContext.selectedSubOrgId);
      setSnapshot({
        user,
        selectedOrgId: nextContext.selectedOrgId,
        selectedSubOrgId: nextContext.selectedSubOrgId,
      });
    },
    setContext: (orgId, subOrgId) => {
      const nextContext = resolveContext(snapshot.user, orgId, subOrgId);
      persistContext(nextContext.selectedOrgId, nextContext.selectedSubOrgId);
      setSnapshot({ selectedOrgId: nextContext.selectedOrgId, selectedSubOrgId: nextContext.selectedSubOrgId });
    },
    logout: () => {
      setAccessToken(null);
      localStorage.removeItem('selectedOrgId');
      localStorage.removeItem('selectedSubOrgId');
      setSnapshot({ user: null, accessToken: null, isAuthenticated: false, selectedOrgId: null, selectedSubOrgId: null });
    },
    hasPermission: (permission) => hasPermissionValue(snapshot.user?.permissions, permission),
    hasRole: (role) => {
      const target = normalizeRole(role);
      return (snapshot.user?.roles || []).some((r) => normalizeRole(r) === target);
    },
  };

  const setSnapshot = (partial: Partial<AuthSnapshot>) => {
    snapshot = { ...snapshot, ...partial };
    writePersistedAuth(snapshot);
    listeners.forEach((listener) => listener());
  };

  return {
    getSnapshot: () => snapshot,
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
};

const authStore = createAuthStore();

if (typeof window !== 'undefined') {
  window.addEventListener('mmc-access-token-changed', (event) => {
    const token = (event as CustomEvent<string | null>).detail || null;
    const current = authStore.getSnapshot();
    if (current.accessToken !== token) {
      current.setAccessTokenState(token);
    }
  });
}

export const useAuthStore = () => useSyncExternalStore(authStore.subscribe, authStore.getSnapshot, authStore.getSnapshot);
